import { useQuery } from "@tanstack/react-query";
import Card from "../Card";
import CardLoader from "../CardLoader";
import Container from "../Container";
import Currency from "../Currency";
import Text from "../Text";
import useAuthHttp from "../../hooks/useAuthHttp";
import moment from "moment";
import Show from "../Show";
import Image from "../Image";
import simbolo from "../../assets/images/simbolo.png";
import { ScrollPanel } from "primereact/scrollpanel";

interface IFutureEventItem {
    event_date: string;
    event_value: number;
    event_type: string;
    asset_name: string;
}

interface IFutureEventResponse {
    future_events: IFutureEventItem[][] | IFutureEventItem[];
    user_id: string;
}

const FutureEventItem = (props: IFutureEventItem) => {
    const months = ["Jan", "Fev", "Mar", "Abr", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

    return (
        <Container between>
            <Container gap={3}>
                <Container center flexColumn className="bg-gray-200 p-2 border-round">
                    <Text fontWeight="normal" size="xs" color="900">{months[moment(props.event_date).toDate().getMonth() - 1]}</Text>
                    <Text color="900" fontWeight="bold" size="2xl">{(moment(props.event_date).toDate().getDate()).toString().padStart(2, "0")}</Text>
                </Container>

                <Container flexColumn className="justify-content-center">
                    <Text color="900" size="2xl" fontWeight="medium">{props.asset_name}</Text>
                    <Text size="xs" color="400">{props.event_type}</Text>
                </Container>
            </Container>

            <Container>
                <Currency value={props.event_value}>
                    {(value) => (
                        <Text
                            color="800"
                            className="p-2 border-1 border-round border-gray-200"
                            fontWeight="semibold"
                        >
                            {value}
                        </Text>
                    )}
                </Currency>
            </Container>
        </Container>
    )
}

export default function FutureEventsCard() {
    const authHttp = useAuthHttp();
    const dataQuery = useQuery({
        queryKey: ["future-events"],
        queryFn: () => {
            return authHttp.doGet<IFutureEventResponse>("/events/future");
        }
    });

    return (
        <Card className="justify-content-start">
            {
                dataQuery.isPending
                    ? (
                        <CardLoader />
                    )
                    : (
                        <Container flexColumn fullWidth gap={3} className="flex-1" paddingVertical="3">
                            <Container className="justify-content-between align-items-center">
                                <Text color="800" fontWeight="semibold">Próximos Eventos</Text>
                            </Container>

                            <Show when={!dataQuery.data?.future_events.length}>
                                <Container center flexColumn className="h-full" gap={3}>
                                    <Image src={simbolo} width={80} />
                                    <Text className="text-center" size="xl">Você não possui nenhum evento programado</Text>
                                </Container>
                            </Show>

                            <Show when={!!dataQuery.data?.future_events.length}>
                                <Container flexColumn fullWidth className="flex-1 justify-content-center h-full" gap={3}>
                                    <ScrollPanel style={{ width: '100%', height: '200px' }}>
                                        <Container gap={3} flexColumn>
                                            {
                                                Array.isArray(dataQuery.data?.future_events[0])
                                                    ? (dataQuery.data?.future_events)
                                                        .flatMap(d => d)
                                                        .filter(d => moment(d.event_date).toDate().getFullYear() == new Date().getFullYear())
                                                        .sort((a, b) => moment(a.event_date).toDate().getTime() - moment(b.event_date).toDate().getTime())
                                                        .map(d => <FutureEventItem key={`${moment(d.event_date).toDate().getTime()}_${d.event_type}`} {...d} />)
                                                    : (dataQuery.data?.future_events as IFutureEventItem[])
                                                        .sort((a, b) => moment(a.event_date).toDate().getTime() - moment(b.event_date).toDate().getTime())
                                                        .map(d => <FutureEventItem key={`${moment(d.event_date).toDate().getTime()}_${d.event_type}`} {...d} />)
                                            }
                                        </Container>
                                    </ScrollPanel>
                                </Container>
                            </Show>
                        </Container>
                    )
            }
        </Card>
    )
}